import { MessageMedium, WhatsAppMedium } from 'constants/messageMediums';
import pickBy from 'lodash/pickBy';
import { LOCALE_CATEGORY_LABELS, LocaleCategory } from 'modules/campaigns/lib/constants';
import { Locale } from 'modules/domains/template';
import { Nullish } from 'utils/tsHelpers';
import { LocaleLabel } from '../LocaleLabel/LocaleLabel';
import { LocaleOption } from '../types';

export const toLocaleOption = ({
  locale,
  suffix,
  categoryId,
  isDisabled,
}: {
  locale: Locale;
  suffix: LocaleOption['Suffix'];
  categoryId?: string;
  isDisabled?: boolean;
}): LocaleOption => ({
  id: locale?.tag,
  value: locale?.tag,
  data: locale,
  label: <LocaleLabel locale={locale} isDisabled={isDisabled} />,
  Suffix: suffix,
  categoryId,
  isDisabled,
});

export const getLocaleOptions = ({
  isExistingTemplate,
  activeLocales,
  projectLocales,
  initialDefaultLocale,
  defaultLocaleSuffix,
  validWhatsAppLocales = [],
  invalidWhatsAppLocales = [],
  messageMedium,
}: {
  defaultLocaleSuffix: string;
  isExistingTemplate: boolean;
  initialDefaultLocale: Nullish<Locale>;
  activeLocales: Locale[];
  projectLocales: Locale[];
  invalidWhatsAppLocales?: Locale[];
  validWhatsAppLocales?: Locale[];
  messageMedium?: MessageMedium | WhatsAppMedium;
}): LocaleOption[] => {
  const isWhatsAppMedium = messageMedium === WhatsAppMedium.WhatsApp;
  const validLocales = isWhatsAppMedium ? validWhatsAppLocales : projectLocales;
  const invalidLocales = isWhatsAppMedium ? invalidWhatsAppLocales : []; // invalid locales are currently only supported for WhatsApp
  const availableOrActiveLocales = isExistingTemplate ? activeLocales : validLocales;

  const availableOrActiveLocalesOptions = availableOrActiveLocales.map((locale) => {
    const suffix = locale.tag === initialDefaultLocale?.tag ? defaultLocaleSuffix : undefined;

    return toLocaleOption({
      locale,
      suffix,
      categoryId: isWhatsAppMedium ? LocaleCategory.Available : undefined, // only show category labels for WhatsApp
      isDisabled: false,
    });
  });

  const invalidWhatsAppLocaleOptions = invalidLocales.map((locale) => {
    return toLocaleOption({
      locale,
      suffix: undefined,
      categoryId: LocaleCategory.NotAvailableForWhatsApp,
      isDisabled: true,
    });
  });

  return [...availableOrActiveLocalesOptions, ...invalidWhatsAppLocaleOptions];
};

/**
 * NOTE: Only WhatsApp support multiple categories.
 * If we always pass in all the category labels to the selectv2 it causes some unintended behavior (e.g no options are displayed).
 * This only return the category labels for WhatsApp medium with locale categories that are both supported and available.
 * */
export const getDefaultLocaleSelectCategoryLabels = ({
  isWhatsAppMedium,
  hasValidWhatsAppLocales,
  hasInvalidWhatsAppLocales,
}: {
  isWhatsAppMedium: boolean;
  hasValidWhatsAppLocales: boolean;
  hasInvalidWhatsAppLocales: boolean;
}) =>
  pickBy(LOCALE_CATEGORY_LABELS, (_value, key) => {
    switch (key) {
      case LocaleCategory.Available:
        return isWhatsAppMedium && hasValidWhatsAppLocales;

      case LocaleCategory.NotAvailableForWhatsApp:
        return isWhatsAppMedium && hasInvalidWhatsAppLocales;

      default:
        return false;
    }
  });
